/**
 * Product Dev — first-paint loading skeletons.
 *
 * DESIGN.md §15.1: skeletons on first paint, spinners only for in-flight
 * mutations. `CardRowsSkeleton` is the reusable shimmer primitive for any
 * sub-card's loading branch (mirrors a media row: thumb + two text lines).
 * `ProductDevDetailSkeleton` mirrors the real detail-page layout so data
 * arrival causes no layout shift.
 */
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** N shimmering media rows. Drop-in replacement for a sub-card `Loader2`. */
export function CardRowsSkeleton({
  rows = 3,
  className,
}: {
  rows?: number;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2.5', className)} aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 shrink-0 rounded-md" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A card-shaped shell: header line + N content rows. */
function CardBlockSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <Skeleton className="h-4 w-28" />
      <CardRowsSkeleton rows={rows} />
    </div>
  );
}

/** Full detail-page skeleton — back bar, header, stage strip, 2+3 card grid. */
export function ProductDevDetailSkeleton() {
  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8" aria-hidden="true">
      {/* Back button */}
      <Skeleton className="h-8 w-28" />

      {/* Header block: hero tile + identity + action cluster */}
      <div className="flex flex-wrap items-start gap-5">
        <Skeleton className="h-24 w-24 shrink-0 rounded-lg" />
        <div className="min-w-0 flex-1 space-y-3">
          <Skeleton className="h-5 w-44 rounded-md" />
          <Skeleton className="h-8 w-72" />
          <div className="flex flex-wrap items-center gap-2">
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-24 rounded-full" />
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
        </div>
        <div className="ml-auto hidden items-center gap-2 sm:flex">
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>

      {/* Stage progress strip */}
      <Skeleton className="h-12 w-full rounded-lg" />

      {/* Two-column card grid: 2 left, 3 right */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <CardBlockSkeleton rows={2} />
          <CardBlockSkeleton rows={4} />
        </div>
        <div className="space-y-6">
          <CardBlockSkeleton rows={3} />
          <CardBlockSkeleton rows={2} />
          <CardBlockSkeleton rows={3} />
        </div>
      </div>
    </div>
  );
}
